import { memo, useCallback, useState } from 'react'

import { AutoDeleteOutlined, ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { LifecyclePolicyDialog } from 'domains/Workspaces/Video/Streams'
import { useVideoStreamCreateMutation, useVideoStreamDeleteMutation } from 'hooks/mutations/content'
import { videoStreamsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Streams'
import { videoStreamRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Streams/Stream'
import { videoStreamQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { videostreamId } = videoStreamRoute.useParams()
  const navigate = useNavigate({ from: videoStreamRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [dialog, setDialog] = useState(false)

  const videoStreamQuery = useQuery(videoStreamQueryKeys.detail({ streamId: videostreamId }))
  const { data: videoStream } = videoStreamQuery

  const { mutate: createVideoStream, ...videoStreamCreateMutation } = useVideoStreamCreateMutation()
  const { mutate: deleteVideoStream } = useVideoStreamDeleteMutation()

  const handleDeleteVideoStream = useCallback(() => {
    if (!videoStream) return

    setAnchorEl(undefined)

    deleteVideoStream(
      { streamId: videoStream.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Video.nameDelete', { name: videoStream.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: videoStreamsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [videoStream, deleteVideoStream, enqueueSnackbar, navigate, t])

  const handleDuplicateVideoStream = useCallback(() => {
    if (!videoStream) return

    setAnchorEl(undefined)

    createVideoStream(
      { videoStreamInsert: videoStream },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createVideoStream, videoStream, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={videoStream?.name}
        entity={t('entity:content.videoStream', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateVideoStream}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {videoStreamCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)

            setTimeout(() => {
              setDialog(true)
            }, 300)
          }}
        >
          <ListItemIcon>
            <AutoDeleteOutlined />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {t('domain:Workspace.Video.setContentLifecyclePolicy', { count: 1 })}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={videostreamId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Video.deleteStream', { count: 1 })}
          onConfirm={handleDeleteVideoStream}
        />
      </ActionMenu>
      <Dialog fullWidth open={dialog} onClose={() => setDialog(false)}>
        {videoStream ? <LifecyclePolicyDialog streamData={videoStream} onSuccess={() => setDialog(false)} /> : null}
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
